import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';

const WhySection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const metrics = [
    { value: 180, suffix: '%', label: 'Lead Artışı', icon: 'ri-arrow-up-line' },
    { value: 45, suffix: '%', label: 'Poliçe Dönüşümü', icon: 'ri-file-check-line' },
    { value: 120, suffix: '+', label: 'Aktif Acente', icon: 'ri-building-line' },
    { value: 3.2, suffix: 'x', label: 'Ortalama ROI', icon: 'ri-line-chart-line' },
  ];

  const features = [
    {
      icon: 'ri-shield-check-line',
      title: 'Sigorta Sektörü Uzmanlığı',
      description: 'Kasko, trafik, dask, sağlık sigortası gibi tüm branşlarda deneyimli ekibimizle yanınızdayız.',
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Müşteri Davranış Analizi',
      description: 'Sigorta müşterilerinin dijital davranışlarını analiz ederek en etkili stratejileri uyguluyoruz.',
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Kanıtlanmış Sonuçlar',
      description: '50+ sigorta acentesi ile çalıştık, ortalama %180 lead artışı sağladık.',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Özel Destek Ekibi',
      description: 'Sigorta diline hakim, sektörü bilen özel destek ekibimiz her zaman yanınızda.',
    },
  ];

  return (
    <section ref={ref} className="py-16 bg-white">
      <div className="max-w-[1440px] mx-auto px-8">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {/* Left - Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/05f9704d918a7285335bc45611f46194.jpg"
                alt="Sigorta Sektörü Uzmanlığı"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
            </div>

            {/* Floating Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="absolute -bottom-4 -right-4 bg-gradient-to-br from-[#007dff] to-[#0066dd] text-white px-5 py-3 rounded-xl shadow-xl"
            >
              <p className="font-poppins text-[11px] font-bold uppercase tracking-wide">Sigorta Dilini Biliyoruz</p>
            </motion.div>
          </motion.div>

          {/* Right - Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <p className="font-poppins font-semibold text-[12px] text-[#007dff] uppercase tracking-wider mb-3">
              Neden Biz?
            </p>
            <h2 className="font-poppins font-bold text-3xl lg:text-4xl text-gray-900 mb-5 leading-tight">
              Sigorta Dilini Biliyoruz, Müşterinizi Tanıyoruz
            </h2>
            <p className="font-poppins text-base text-gray-600 mb-6 leading-relaxed">
              Sadece sigorta sektörüne odaklanmış bir ajans olarak, müşterilerinizin ihtiyaçlarını ve davranışlarını derinlemesine anlıyoruz. Her kampanya, her içerik sigorta diline hakim ekibimiz tarafından özenle hazırlanıyor.
            </p>

            {/* Metrics */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {metrics.map((metric, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                  className="bg-gradient-to-br from-[#007dff]/5 to-[#007dff]/10 rounded-xl p-5 hover:shadow-lg transition-all duration-300 cursor-pointer group"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg shadow-sm group-hover:scale-110 group-hover:bg-[#007dff] transition-all duration-300 flex-shrink-0">
                      <i className={`${metric.icon} text-xl text-[#007dff] group-hover:text-white transition-colors duration-300`}></i>
                    </div>
                    <div>
                      <p className="font-poppins font-bold text-2xl text-gray-900 mb-1">
                        {inView ? <CountUp end={metric.value} duration={2} decimals={metric.suffix === 'x' ? 1 : 0} /> : 0}
                        {metric.suffix}
                      </p>
                      <p className="font-poppins text-sm text-gray-600">{metric.label}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Features */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: 'ri-shield-check-line', title: 'Sigorta Sektörü Uzmanlığı', desc: '5+ yıl sigorta odaklı deneyim' },
                { icon: 'ri-user-search-line', title: 'Müşteri Davranış Analizi', desc: 'Hedef kitle derinlemesine analiz' },
                { icon: 'ri-line-chart-line', title: 'Kanıtlanmış Sonuçlar', desc: '%180 ortalama büyüme oranı' },
                { icon: 'ri-customer-service-2-line', title: 'Özel Destek Ekibi', desc: '7/24 sigorta uzmanı desteği' },
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                  className="flex items-start gap-3 p-3 bg-white rounded-xl hover:shadow-md transition-all duration-300 cursor-pointer group"
                >
                  <div className="w-9 h-9 flex items-center justify-center bg-[#007dff]/10 rounded-lg flex-shrink-0 group-hover:bg-[#007dff] transition-all duration-300">
                    <i className={`${feature.icon} text-base text-[#007dff] group-hover:text-white transition-colors duration-300`}></i>
                  </div>
                  <div>
                    <h4 className="font-poppins font-semibold text-[13px] text-gray-900 mb-1">{feature.title}</h4>
                    <p className="font-poppins text-[11px] text-gray-600">{feature.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Bottom Stats Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="mt-6 bg-gradient-to-r from-[#007dff] to-[#0066dd] rounded-xl p-4"
            >
              <div className="grid grid-cols-3 gap-4 text-center">
                {[
                  { value: '50+', label: 'Sigorta Acentesi' },
                  { value: '5 Yıl', label: 'Sektör Deneyimi' },
                  { value: '%98', label: 'Müşteri Memnuniyeti' },
                ].map((stat, index) => (
                  <div key={index}>
                    <p className="font-poppins font-bold text-xl text-white mb-1">{stat.value}</p>
                    <p className="font-poppins text-[11px] text-white/90">{stat.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhySection;